import * as React from "react";
import { Text } from "../../../ui/components/Text";
import { Button } from "../../../ui/components/Button";
import { Modal } from "../../../ui/components/Modal";
import { Callout } from "../../../ui/components/Callout";
import { useAuth } from "../../lib/auth/hooks";
import * as userApi from "../../lib/api/users";
import { getApiErrorMessage } from "../../lib/api/api-error";
import { uploadOrgLogoFile } from "../../lib/profile-avatar-upload";
import { MediaUploader } from "../../../ui/components/MediaUploader";
import type { MediaEntry } from "../../../ui/components/MediaUploader";
import {
  OrganizationFormFields,
  emptyOrganizationFormValues,
  organizationFormValuesToPayload,
  type OrganizationFormValues,
} from "../organizations/OrganizationFormFields";
import { useI18n } from "../../lib/i18n";

export interface CreateOrganizationModalProps {
  open: boolean;
  setOpen: (v: boolean) => void;
  onCreated?: (orgId: string) => void;
}

export function CreateOrganizationModal({
  open,
  setOpen,
  onCreated,
}: CreateOrganizationModalProps) {
  const { t } = useI18n();
  const { accessToken, refreshUser, refreshAccessToken } = useAuth();
  const token = accessToken ?? localStorage.getItem("access_token");

  const [values, setValues] = React.useState<OrganizationFormValues>(emptyOrganizationFormValues());
  const [nameAvailable, setNameAvailable] = React.useState<boolean | null>(null);
  const [nameChecking, setNameChecking] = React.useState(false);
  const [creating, setCreating] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const [logoError, setLogoError] = React.useState<string | null>(null);
  const nameCheckTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetForm = React.useCallback(() => {
    setValues(emptyOrganizationFormValues());
    setNameAvailable(null);
    setNameChecking(false);
    setError(null);
    setLogoError(null);
  }, []);

  React.useEffect(() => {
    if (!open) resetForm();
  }, [open, resetForm]);

  const patchValues = React.useCallback((patch: Partial<OrganizationFormValues>) => {
    setValues((prev) => ({ ...prev, ...patch }));
  }, []);

  const handleNameChange = (value: string) => {
    patchValues({ name: value });
    setNameAvailable(null);
    setError(null);

    if (nameCheckTimer.current) clearTimeout(nameCheckTimer.current);

    const trimmed = value.trim();
    if (trimmed.length < 2) {
      setNameAvailable(null);
      return;
    }

    setNameChecking(true);
    nameCheckTimer.current = setTimeout(async () => {
      if (!token) return;
      try {
        const result = await userApi.checkOrganizationName(token, trimmed);
        setNameAvailable(result.available);
      } catch {
        setNameAvailable(null);
      } finally {
        setNameChecking(false);
      }
    }, 400);
  };

  const uploadLogoFile = React.useCallback(async (file: File): Promise<string> => {
    if (!token) throw new Error("Sign in again to upload.");
    return uploadOrgLogoFile(token, file);
  }, [token]);

  const logoItems: MediaEntry[] = React.useMemo(
    () => (values.logoUrl.trim() ? [{ id: "logo", url: values.logoUrl.trim() }] : []),
    [values.logoUrl],
  );

  // The organization doesn't exist yet — there's no update-organization endpoint to call
  // until the whole create-org form submits, so just stage the new URL in local form state.
  const handleLogoApply = React.useCallback((newItems: MediaEntry[]) => {
    patchValues({ logoUrl: newItems[0]?.url ?? "" });
  }, [patchValues]);

  const handleLogoChange = React.useCallback((next: MediaEntry[]) => {
    patchValues({ logoUrl: next[0]?.url ?? "" });
  }, [patchValues]);

  const handleLogoRemove = React.useCallback(() => {
    patchValues({ logoUrl: "" });
  }, [patchValues]);

  const handleCreate = async () => {
    if (!token) {
      setError("Session expired. Please sign in again.");
      return;
    }
    if (!values.name.trim()) {
      setError("Organization name is required.");
      return;
    }
    if (nameAvailable === false) {
      setError("This name is already taken. Choose a different name.");
      return;
    }

    setCreating(true);
    setError(null);
    try {
      const result = await userApi.createOrganization(token, organizationFormValuesToPayload(values));
      // New token carries the new org's role claims (e.g. certification:enroll).
      await refreshAccessToken().catch(() => refreshUser());
      setOpen(false);
      onCreated?.(result.id);
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to create organization"));
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      minWidth="520px"
      maxWidth="520px"
      minHeight="auto"
      maxHeight="85vh"
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 16, padding: "8px 4px 4px" }}>
        <Text level="h3" weight="bold">
          Create new organization
        </Text>

        {error && <Callout variant="error">{error}</Callout>}

        <OrganizationFormFields
          values={values}
          onChange={patchValues}
          logoUploader={
            <MediaUploader
              items={logoItems}
              onChange={handleLogoChange}
              onApply={handleLogoApply}
              uploadFile={uploadLogoFile}
              maxItems={1}
              shape="square"
              onRemove={handleLogoRemove}
              error={logoError}
              onError={setLogoError}
              placeholder={t("pro.orgs.logoPlaceholder")}
            />
          }
          showNameAvailability
          nameAvailable={nameAvailable}
          nameChecking={nameChecking}
          onNameChange={handleNameChange}
        />

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
          <Button
            type="button"
            color="neutral"
            variant="ghost"
            onClick={() => setOpen(false)}
            disabled={creating}
          >
            Cancel
          </Button>
          <Button
            type="button"
            color="primary"
            variant="solid"
            loading={creating}
            disabled={!values.name.trim() || nameAvailable === false || nameChecking}
            onClick={handleCreate}
          >
            Create organization
          </Button>
        </div>
      </div>
    </Modal>
  );
}
